/**
 * Landing — V2.0 Premium Experience (Phase 1)
 *
 * Complete replacement of the V1.1 landing page. Light-first, warm-cream
 * palette, editorial illustration style, "financial coach beside the user"
 * tone. Uses V2.0 primitives from /app/frontend/src/components/v2/ and the
 * five custom-generated PNGs in /app/frontend/src/assets/illustrations/.
 *
 * Route: `/`
 * Analytics: page-view + route-change firing is handled globally by
 *   <AnalyticsTracker /> in App.js — no per-CTA trackEvent shims live here.
 * UTM persistence: also handled at the App shell level and at Signup mount.
 *
 * Design guardrails:
 *   - Light-first. `.dark` class only appears if the user opts in via the
 *     ThemeToggle. CSS-variable driven — V1.1 surfaces stay untouched.
 *   - Zero dev-terminology in visible copy (except the intentional "Coming
 *     soon" chip on the homeownership sub-section, which is a real product
 *     signal).
 *   - Semantic HTML: single <h1> (hero), <h2> per section.
 *   - Illustrations carry empty `alt=""` because they are purely decorative;
 *     screen readers correctly skip them.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Target,
  TrendingUp,
  Users,
  Home,
  ArrowRight,
} from "lucide-react";

import { useAuth } from "@/auth/AuthContext";
import V2Hero from "@/components/v2/V2Hero";
import TestimonialCard from "@/components/v2/TestimonialCard";
import MilestoneCard from "@/design-system/components/MilestoneCard";
import ThemeToggle from "@/components/v2/ThemeToggle";
import { IdentityIQProvisioningModal } from "@/components/credit/IdentityIQConnectCard";

import heroMain from "@/assets/illustrations/v2_hero_main.png";
import creditJourney from "@/assets/illustrations/v2_credit_journey.png";
import businessFunding from "@/assets/illustrations/v2_business_funding.png";
import homeownership from "@/assets/illustrations/v2_homeownership.png";
import howItWorks from "@/assets/illustrations/v2_how_it_works.png";

const DISCLAIMER =
  "This app does not provide legal, financial, or credit repair guarantees. It provides educational analysis and draft documents based on information you provide. Approval, funding, score increases, and account removals are not guaranteed. Please review all information before submitting documents or applying for funding.";

/* ------------------------------- helpers ------------------------------- */

function TopNav() {
  return (
    <header
      data-testid="landing-nav"
      className="sticky top-0 z-40 border-b border-v2-border bg-v2-background/85 backdrop-blur"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10 py-4 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="landing-nav-brand"
          className="inline-flex items-baseline gap-1 text-v2-heading-sm font-semibold text-v2-text"
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          <span>Informed OS</span>
          <span
            aria-hidden="true"
            className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-v2-accent shadow-[0_0_10px_var(--v2-accent)]"
          />
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle />
          <Link
            to="/login"
            data-testid="landing-nav-member-login"
            className="inline-flex items-center gap-1.5 rounded-v2-md px-3 py-1.5 text-v2-body-sm font-semibold text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
          >
            Member Login
          </Link>
        </div>
      </div>
    </header>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="text-v2-caption uppercase text-v2-accentStrong tracking-[0.16em] mb-4">
      {children}
    </div>
  );
}

function SectionHeading({ children, testId }) {
  return (
    <h2
      data-testid={testId}
      className="text-v2-display-md md:text-v2-display-lg text-v2-text max-w-3xl"
      style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
    >
      {children}
    </h2>
  );
}

function FeatureCard({ Icon, title, body, testId, iconTint = "accent" }) {
  const tintMap = {
    accent:  "bg-v2-accentSoft text-v2-accentStrong",
    success: "bg-v2-successSoft text-v2-success",
    info:    "bg-v2-infoSoft text-v2-info",
    warning: "bg-v2-warningSoft text-v2-warning",
  };
  return (
    <article
      data-testid={testId}
      className="rounded-v2-2xl bg-v2-surface border border-v2-border p-8 md:p-10 shadow-v2-subtle hover:shadow-v2-card transition-shadow flex flex-col gap-4 h-full"
    >
      <span
        aria-hidden="true"
        className={`inline-flex h-11 w-11 items-center justify-center rounded-v2-md ${tintMap[iconTint]}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <h3
        className="text-v2-heading-md text-v2-text"
        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
      >
        {title}
      </h3>
      <p className="text-v2-body-md text-v2-textMuted leading-relaxed">{body}</p>
    </article>
  );
}

function DeepDive({
  image,
  title,
  paragraph,
  bullets,
  chip,
  cta,
  imageSide = "left",
  testId,
  eyebrow,
}) {
  const isImageLeft = imageSide === "left";
  return (
    <section
      data-testid={testId}
      className="grid gap-10 md:gap-14 md:grid-cols-2 items-center"
    >
      <div className={isImageLeft ? "md:order-1" : "md:order-2"}>
        <div className="rounded-v2-2xl overflow-hidden bg-v2-surfaceSubtle border border-v2-border shadow-v2-card">
          <img
            src={image}
            alt=""
            loading="lazy"
            className="block w-full h-auto"
          />
        </div>
      </div>
      <div className={isImageLeft ? "md:order-2" : "md:order-1"}>
        {eyebrow ? <SectionEyebrow>{eyebrow}</SectionEyebrow> : null}
        <h2
          className="text-v2-display-md text-v2-text"
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          {title}
        </h2>
        <p className="mt-5 text-v2-body-lg text-v2-textMuted leading-relaxed">
          {paragraph}
        </p>
        {bullets && bullets.length ? (
          <ul className="mt-6 grid gap-2 text-v2-body-md text-v2-text">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <span
                  aria-hidden="true"
                  className="mt-2 h-1.5 w-1.5 rounded-full bg-v2-accent flex-shrink-0"
                />
                <span className="text-v2-body-md text-v2-textMuted">{b}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {chip ? (
          <span
            data-testid={`${testId}-chip`}
            className="mt-6 inline-flex items-center rounded-v2-md bg-v2-warningSoft text-v2-warning px-3 py-1.5 text-v2-caption uppercase font-semibold"
          >
            {chip}
          </span>
        ) : null}
        {cta && cta.label ? (
          <div className="mt-7">
            <a
              data-testid={`${testId}-cta`}
              href={cta.href || "#"}
              onClick={(e) => {
                if (typeof cta.onClick === "function") {
                  cta.onClick(e);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-v2-md bg-v2-accent px-5 py-2.5 text-v2-body-md font-medium text-v2-accentInk hover:bg-v2-accentStrong transition-colors"
            >
              {cta.label}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* -------------------------------- page --------------------------------- */

export default function Landing() {
  const navigate = useNavigate();
  const { authenticated } = useAuth();

  // Phase F1 (Credit Upload Lockdown, IdentityIQ Exclusive).
  // The hero's secondary CTA now opens the IdentityIQ provisioning modal
  // in-place — no navigation, no manual upload path. Modal copy is fixed
  // by product direction; see `IdentityIQProvisioningModal`.
  const [identityIQModalOpen, setIdentityIQModalOpen] = useState(false);

  const onConnectIdentityIQ = (e) => {
    e?.preventDefault?.();
    setIdentityIQModalOpen(true);
  };

  // Cluster B iter3 tweak 1 — Same auth-aware pattern for the homeownership
  // deep-dive CTA. Mortgage Readiness is a live feature (`/mortgage-intake`),
  // so this CTA is now a real deep-link, not marketing filler.
  const onMortgageReadiness = (e) => {
    e?.preventDefault?.();
    navigate(
      authenticated ? "/mortgage-intake" : "/login?next=%2Fmortgage-intake",
    );
  };

  const year = new Date().getFullYear();

  return (
    <div
      data-testid="landing-root"
      className="min-h-screen bg-v2-background text-v2-text"
    >
      <TopNav />

      <main>
        {/* Section 1 — Hero */}
        <section className="mx-auto max-w-6xl px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
          <V2Hero
            eyebrow="Funding Readiness Financial Analyst"
            title="Analyze. Repair. Prepare. Fund."
            subtitle="Your AI-powered funding readiness platform. Understand where you stand, fix what's blocking you, and get positioned for the capital you need."
            primaryCta={{ label: "Start Funding Readiness Check", href: "/signup" }}
            secondaryCta={{
              label: "Connect IdentityIQ",
              href: "#connect-identityiq",
              onClick: onConnectIdentityIQ,
            }}
            illustration={
              <img
                src={heroMain}
                alt=""
                className="block w-full h-auto rounded-v2-xl"
              />
            }
            layout="split"
          />
        </section>

        {/* Section 2 — What we do */}
        <section
          data-testid="landing-section-what-we-do"
          className="mx-auto max-w-6xl px-6 md:px-10 py-20 md:py-28"
        >
          <div className="max-w-2xl">
            <SectionEyebrow>What we do</SectionEyebrow>
            <SectionHeading testId="landing-what-we-do-heading">
              Four systems that get you funding-ready.
            </SectionHeading>
            <p className="mt-5 text-v2-body-lg text-v2-textMuted max-w-2xl">
              Every part of the platform answers one question:
              &ldquo;What&rsquo;s blocking me from the capital I need?&rdquo; — and then walks you through fixing it.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            <FeatureCard
              testId="landing-feature-readiness"
              Icon={Target}
              iconTint="accent"
              title="Funding Readiness Score"
              body="Get a personalized 0-100 score based on your credit, revenue, and business profile. See exactly what's blocking your access to capital."
            />
            <FeatureCard
              testId="landing-feature-credit"
              Icon={TrendingUp}
              iconTint="success"
              title="Credit Readiness"
              body="Connect your IdentityIQ account. We identify negative items across all three bureaus, generate FCRA-compliant dispute letters, and track your progress bureau-by-bureau."
            />
            <FeatureCard
              testId="landing-feature-partners"
              Icon={Users}
              iconTint="info"
              title="Funding Partner Match"
              body="Get matched to real funding partners based on your eligibility. See who you qualify for today and what to fix to unlock more."
            />
            <FeatureCard
              testId="landing-feature-roadmap"
              Icon={Home}
              iconTint="warning"
              title="Progress Roadmap"
              body="A step-by-step plan showing your fastest path from where you are to funding-ready. Milestones, timing, and next actions — always clear."
            />
          </div>
        </section>

        {/* Section 3 — Deep-dive alternating */}
        <section
          data-testid="landing-section-deep-dive"
          className="mx-auto max-w-6xl px-6 md:px-10 py-20 md:py-28 space-y-24 md:space-y-32"
        >
          <DeepDive
            testId="landing-deep-dive-credit"
            eyebrow="Credit improvement"
            image={creditJourney}
            imageSide="left"
            title="Your credit is more than a number."
            paragraph="We analyze every tradeline, inquiry, collection, and public record across all three bureaus. Then we help you understand what's fixable, what's priority, and how to dispute inaccurate items using real FCRA references."
            bullets={[
              "3-bureau analysis via your IdentityIQ connection",
              "FCRA-compliant dispute letters",
              "30-day retention of your imported bureau data",
              "Score improvement tracking over time",
            ]}
          />
          <DeepDive
            testId="landing-deep-dive-funding"
            eyebrow="Business funding"
            image={businessFunding}
            imageSide="right"
            title="Get funded when you're actually ready."
            paragraph="Match with real funding partners like ROK Financial and Seven Figure Funding. We show you exactly who you qualify for today — and what to fix to unlock more options tomorrow."
            bullets={[
              "Real partner requirements",
              "Live eligibility check",
              "No hidden fees",
              "Ready-when-you-are matching",
            ]}
          />
          <DeepDive
            testId="landing-deep-dive-homeownership"
            eyebrow="Homeownership readiness"
            image={homeownership}
            imageSide="left"
            title="Business today. Homeownership tomorrow."
            paragraph="Complete your Mortgage Readiness profile to unlock personalized purchase-power estimates and lender matches."
            cta={{
              label: "Start Mortgage Readiness",
              onClick: onMortgageReadiness,
              href: authenticated ? "/mortgage-intake" : "/login?next=%2Fmortgage-intake",
            }}
          />
        </section>

        {/* Section 4 — How it works */}
        <section
          data-testid="landing-section-how-it-works"
          className="mx-auto max-w-6xl px-6 md:px-10 py-20 md:py-28"
        >
          <div className="max-w-2xl">
            <SectionEyebrow>How it works</SectionEyebrow>
            <SectionHeading testId="landing-how-it-works-heading">
              Four steps from where you are to funded.
            </SectionHeading>
          </div>

          <div className="mt-12 grid gap-10 md:grid-cols-[1.05fr_1fr] items-center">
            <div className="rounded-v2-2xl overflow-hidden bg-v2-surfaceSubtle border border-v2-border shadow-v2-card">
              <img
                src={howItWorks}
                alt=""
                loading="lazy"
                className="block w-full h-auto"
              />
            </div>
            <div className="grid gap-4">
              <MilestoneCard
                status="upcoming"
                title="Connect your IdentityIQ account"
                nextStep="One connection pulls all three bureaus — TransUnion, Equifax, and Experian — into your workspace."
              />
              <MilestoneCard
                status="upcoming"
                title="Get your readiness score"
                nextStep="See your 0-100 score plus what's blocking you."
              />
              <MilestoneCard
                status="upcoming"
                title="Fix what's blocking you"
                nextStep="Dispute inaccuracies with FCRA-compliant letters. Track improvements."
              />
              <MilestoneCard
                status="upcoming"
                title="Get matched to funding"
                nextStep="See exactly which partners you qualify for, and apply when ready."
              />
            </div>
          </div>
        </section>

        {/* Section 5 — Success stories */}
        <section
          data-testid="landing-section-testimonials"
          className="mx-auto max-w-6xl px-6 md:px-10 py-20 md:py-28 bg-v2-surfaceSubtle rounded-v2-2xl"
        >
          <div className="max-w-2xl">
            <SectionEyebrow>Success stories</SectionEyebrow>
            <SectionHeading testId="landing-testimonials-heading">
              People walking the path you're on.
            </SectionHeading>
            <p className="mt-5 text-v2-body-lg text-v2-textMuted max-w-2xl">
              Real outcomes from founders and small-business owners who put in
              the work. Composite quotes for now — verified case studies in
              the next release.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            <TestimonialCard
              quote="I went from a 580 to a 674 in eight months and finally got approved for $50K in working capital. The dispute letters alone saved me months of confusion."
              metric={{ value: "+94 pts", label: "credit score" }}
              name="Sarah M."
              role="Small business owner, Atlanta GA"
            />
            <TestimonialCard
              featured
              quote="The readiness roadmap made it crystal clear what I needed to fix before applying. My third try with a real partner was an approval — first two attempts had been rejections."
              metric={{ value: "$150K", label: "funding approved" }}
              name="James R."
              role="Freight & logistics, Dallas TX"
            />
            <TestimonialCard
              quote="As a nonprofit founder, I didn't understand how funders viewed my credit. This platform gave me a plan and, honestly, the confidence to keep going."
              metric={{ value: "3 rounds", label: "of funding secured" }}
              name="Priya D."
              role="Nonprofit founder, Chicago IL"
            />
          </div>
        </section>

        {/* Section 6 — Final CTA (rendered before the disclaimer/footer, per spec) */}
        <section
          data-testid="landing-section-final-cta"
          className="mx-auto max-w-4xl px-6 md:px-10 py-20 md:py-28 text-center"
        >
          <h2
            className="text-v2-display-md md:text-v2-display-lg text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Ready to see where you stand?
          </h2>
          <p className="mt-5 text-v2-body-lg text-v2-textMuted max-w-xl mx-auto">
            Free readiness check. No credit card required.
          </p>
          <div className="mt-8 flex justify-center">
            <Link
              to="/signup"
              data-testid="landing-final-cta-primary"
              className="inline-flex items-center justify-center gap-2 rounded-v2-md bg-v2-accent px-6 py-3 text-v2-body-md font-semibold text-v2-accentInk hover:bg-v2-accentStrong shadow-v2-card hover:shadow-v2-lifted transition-all"
            >
              Start Free Check
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* Section 7 — Compliance disclaimer (small, above footer, always visible) */}
        <section
          data-testid="landing-section-disclaimer"
          className="mx-auto max-w-6xl px-6 md:px-10 pb-6"
        >
          <p
            data-testid="landing-disclaimer"
            className="text-v2-body-sm text-v2-textSubtle leading-relaxed max-w-3xl mx-auto text-center"
          >
            {DISCLAIMER}
          </p>
          {/* Dual-brand attribution — product name vs. legal entity.
              Same size/tone as the disclaimer; sits immediately below
              so both compliance-adjacent notes read together. */}
          <p
            data-testid="landing-footer-legal-attribution"
            className="mt-3 text-v2-caption text-v2-textSubtle max-w-3xl mx-auto text-center"
          >
            Informed OS™ is a product of Informed Accelerator, Inc.
          </p>
        </section>
      </main>

      {/* Section 8 — Footer */}
      <footer
        data-testid="landing-footer"
        className="border-t border-v2-border bg-v2-surface"
      >
        <div className="mx-auto max-w-6xl px-6 md:px-10 py-10 grid gap-6 md:grid-cols-3 items-center">
          <div>
            <div
              className="text-v2-heading-sm text-v2-text"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              Informed OS
            </div>
            <div className="mt-1 text-v2-body-sm text-v2-textMuted">
              Analyze. Repair. Prepare. Fund.
            </div>
          </div>
          <nav
            data-testid="landing-footer-links"
            className="flex flex-wrap items-center justify-start md:justify-center gap-5 text-v2-body-sm"
          >
            <Link to="/legal/privacy" className="text-v2-textMuted hover:text-v2-text">
              Privacy
            </Link>
            <Link to="/legal/terms" className="text-v2-textMuted hover:text-v2-text">
              Terms
            </Link>
            <a
              href="mailto:support@informedos.com"
              className="text-v2-textMuted hover:text-v2-text"
            >
              Support
            </a>
          </nav>
          <div className="flex items-center justify-start md:justify-end gap-3">
            <ThemeToggle compact />
            <Link
              to="/login"
              data-testid="landing-footer-member-login"
              className="inline-flex items-center gap-1.5 rounded-v2-md px-3 py-1.5 text-v2-body-sm font-semibold border border-v2-border text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
            >
              Member Login
            </Link>
          </div>
        </div>
        <div className="border-t border-v2-border">
          <div className="mx-auto max-w-6xl px-6 md:px-10 py-4 text-v2-caption text-v2-textSubtle">
            © {year} Informed OS
          </div>
        </div>
      </footer>

      {identityIQModalOpen ? (
        <IdentityIQProvisioningModal
          testIdPrefix="landing-identityiq"
          onClose={() => setIdentityIQModalOpen(false)}
        />
      ) : null}
    </div>
  );
}
